/**
 * usePresence Hook - React Hook for Presence Management
 *
 * Refactored to use Upstash Realtime SDK directly.
 * Maintains backwards compatibility with existing API.
 *
 * @module features/collab/transport/hooks/usePresence
 * @see SPEC-007-6 Requirements 8.1-8.6
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type {
  Position,
  Selection,
  PresenceData,
  RoomParticipant,
  PresenceUpdateEvent,
  PresenceJoinEvent,
  PresenceLeaveEvent,
} from '@linkode/shared';
import type { RoomConnection } from './useRoomConnection';

// ============================================================================
// Types
// ============================================================================

/** Return type for usePresence hook */
export interface UsePresenceResult {
  participants: RoomParticipant[];
  localPresence: PresenceData | null;
  updateCursor: (cursor: Position | null) => void;
  updateSelection: (selection: Selection | null) => void;
  participantCount: number;
}

// ============================================================================
// Helpers
// ============================================================================

function joinEventToParticipant(event: PresenceJoinEvent): RoomParticipant {
  return {
    userId: event.userId,
    presence: {
      cursor: null,
      selection: null,
      displayName: event.displayName,
      colorId: event.colorId,
      updatedAt: Date.now(),
    },
    joinedAt: Date.now(),
  };
}

function updateParticipantPresence(
  participant: RoomParticipant,
  event: PresenceUpdateEvent
): RoomParticipant {
  return {
    ...participant,
    presence: {
      cursor: event.cursor,
      selection: event.selection,
      displayName: event.displayName,
      colorId: event.colorId,
      updatedAt: Date.now(),
    },
  };
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * React Hook for managing presence using Upstash Realtime SDK
 * Maintains backwards compatibility with legacy API that accepts RoomConnection.
 *
 * @param connection - Room connection instance (for backwards compatibility)
 * @returns Presence state and update methods
 */
export function usePresence(connection: RoomConnection | null): UsePresenceResult {
  const [participantsMap, setParticipantsMap] = useState<Map<string, RoomParticipant>>(new Map());

  // Get user info from connection
  const userId = connection?.getUserId() ?? '';
  const localPresenceData = connection?.getLocalPresence() ?? null;

  // These handlers are defined for future use when direct SDK event handling is needed
  // Currently, events are handled through connection.on() subscriptions below
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePresenceJoin = useCallback(
    (data: PresenceJoinEvent) => {
      if (data.userId === userId) return;
      setParticipantsMap((prev) => {
        const next = new Map(prev);
        next.set(data.userId, joinEventToParticipant(data));
        return next;
      });
    },
    [userId]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePresenceLeave = useCallback((data: PresenceLeaveEvent) => {
    setParticipantsMap((prev) => {
      const next = new Map(prev);
      next.delete(data.userId);
      return next;
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePresenceUpdate = useCallback(
    (data: PresenceUpdateEvent) => {
      if (data.userId === userId) return;
      setParticipantsMap((prev) => {
        const existing = prev.get(data.userId);
        const next = new Map(prev);
        if (existing) {
          next.set(data.userId, updateParticipantPresence(existing, data));
        } else {
          next.set(data.userId, {
            userId: data.userId,
            presence: {
              cursor: data.cursor,
              selection: data.selection,
              displayName: data.displayName,
              colorId: data.colorId,
              updatedAt: Date.now(),
            },
            joinedAt: Date.now(),
          });
        }
        return next;
      });
    },
    [userId]
  );

  // Subscribe to connection's events (presence events are handled by useRoomConnection)
  useEffect(() => {
    if (!connection) {
      setParticipantsMap(new Map());
      return;
    }

    const unsubJoin = connection.on('participantJoined', (participant: unknown) => {
      const p = participant as RoomParticipant;
      if (p.userId === userId) return;
      setParticipantsMap((prev) => {
        const next = new Map(prev);
        next.set(p.userId, p);
        return next;
      });
    });

    const unsubLeave = connection.on('participantLeft', (leftUserId: unknown) => {
      setParticipantsMap((prev) => {
        const next = new Map(prev);
        next.delete(leftUserId as string);
        return next;
      });
    });

    const unsubUpdate = connection.on(
      'presenceUpdated',
      (updatedUserId: unknown, presence: unknown) => {
        if (updatedUserId === userId) return;
        const update = presence as PresenceUpdateEvent;
        const presenceData: PresenceData = {
          cursor: update.cursor ?? null,
          selection: update.selection ?? null,
          displayName: update.displayName,
          colorId: update.colorId,
          updatedAt: Date.now(),
        };

        setParticipantsMap((prev) => {
          const next = new Map(prev);
          const existing = next.get(updatedUserId as string);
          next.set(updatedUserId as string, {
            userId: updatedUserId as string,
            presence: presenceData,
            joinedAt: existing?.joinedAt ?? Date.now(),
          });
          return next;
        });
      }
    );

    return () => {
      unsubJoin();
      unsubLeave();
      unsubUpdate();
    };
  }, [connection, userId]);

  // Update cursor - delegates to connection
  const updateCursor = useCallback(
    (cursor: Position | null) => {
      connection?.updateCursor(cursor);
    },
    [connection]
  );

  // Update selection - delegates to connection
  const updateSelection = useCallback(
    (selection: Selection | null) => {
      connection?.updateSelection(selection);
    },
    [connection]
  );

  // Convert map to array
  const participants = useMemo(() => Array.from(participantsMap.values()), [participantsMap]);

  return {
    participants,
    localPresence: localPresenceData as PresenceData | null,
    updateCursor,
    updateSelection,
    participantCount: participants.length,
  };
}
